import { useState, useEffect, useCallback, useMemo } from 'react'

const usePaginatedData = ({
  fetchFunction,
  dependencies = [],
  limit = 10,
  initialPage = 1
}) => {
  const [data, setData] = useState([])
  const [pagination, setPagination] = useState({})
  const [currentPage, setCurrentPage] = useState(initialPage)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async (page = currentPage) => {
    try {
      setIsLoading(true)
      setError(null)
      
      const response = await fetchFunction({
        page,
        limit,
        ...dependencies
      })

      setPagination(response.paginator || {})
      setData(response.data || response.tournaments || response.events || [])
    } catch (err) {
      console.error('Error fetching data:', err)
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }, [fetchFunction, currentPage, limit, ...dependencies])

  const handlePageChange = useCallback((_, page) => {
    setCurrentPage(page)
  }, [])

  useEffect(() => {
    fetchData(currentPage)
  }, [fetchData, currentPage])

  const hasData = useMemo(() => data?.length > 0, [data?.length])

  const shouldShowPagination = useMemo(() => {
    return hasData && (data.length > limit || pagination?.hasNextPage)
  }, [hasData, data.length, limit, pagination?.hasNextPage])

  return {
    data,
    pagination,
    currentPage,
    isLoading,
    error,
    hasData,
    shouldShowPagination,
    handlePageChange,
    refetch: () => fetchData(currentPage)
  }
}

export default usePaginatedData
